// COPIED from image-os-web src/lib/design-doc.ts (the QR renderer region: QrColor/QrShape,
// QR_ECC and qrSvg — the SAME generator that prints the door signs, so what a pro downloads
// here is pixel-for-pixel the code on their door). Migration=copy rule: extracted by script,
// not retyped. Seams (3): qrSvg exported; ACCENTS.pink -> literal '#dd1d8a' (the IMAGE OS
// standard pink — design-doc's ACCENTS table sits outside the copied region); the qrcodegen
// import carried over from the source file's own header.

import qrcodegen from 'qrcode-generator';

/** How a QR is drawn (Peter, 2026-08-12: "Black or Pink. round or square"; 2026-08-15, walking
 *  Canva's template strip: "You should have style options" — so the shapes grew into families:
 *  'disc' a round tile with a thin ring, 'burst' the radial-dash circle, 'frame' a bordered
 *  square, 'badge' the inverted dark disc. Every one is drawn by the same generator that prints,
 *  and every one was rasterised and run through a real decoder before it was allowed in. */
export type QrColor = 'ink' | 'pink';
export type QrShape = 'square' | 'round' | 'disc' | 'burst' | 'frame' | 'badge';

/**
 * Error correction 'Q' — chosen by measuring, not by instinct.
 *
 * The artwork gives the code a 34pt slot, which is 12mm on paper. Spread the quiet zone and the
 * modules across that and the module size decides whether a phone can read it:
 *   L → 25×25 → 0.365mm   M → 29×29 → 0.325mm   Q → 29×29 → 0.325mm   H → 33×33 → 0.293mm
 * 'H' (the first choice here, on the reasoning that a door sign gets scuffed) makes the smallest
 * modules of the four, at 0.293mm — under the ~0.4mm usually recommended for reliable phone
 * scanning. 'Q' costs nothing to get back: it produces the SAME 29×29 grid as 'M' while tolerating
 * 25% damage instead of 15%. Strictly better than both alternatives at this physical size.
 */
const QR_ECC = 'Q';

/**
 * A QR code as inline SVG — generated HERE, never fetched: the sheet must stay self-contained
 * (the print window and the preview iframe both render it), and a third-party QR service would
 * put the studio's link in someone else's logs. qrcode-generator is synchronous and dependency-
 * free, which is what lets the one renderer stay a pure function. Only real links get a code.
 *
 * ROUND MODULES ARE SAFE, and that is not obvious. A QR reader samples the CENTRE of each module,
 * so shrinking a dark square to an inscribed circle changes nothing it looks at — which is why
 * every consumer "styled QR" does this. The three finder patterns (the big corner eyes) are the
 * exception: they are located by their run-length ratio across a scanline, so they stay SQUARE
 * whatever the shape, and rounding them is what breaks styled codes elsewhere.
 */
export function qrSvg(url: string, size: number, opts: { color?: QrColor; shape?: QrShape } = {}): string {
  if (!/^https?:\/\//i.test(url)) return '';
  const qr = qrcodegen(0, QR_ECC);
  qr.addData(url);
  qr.make();
  const n = qr.getModuleCount();
  // THE QUIET ZONE. The spec wants four clear modules of margin, and it is not decoration: a
  // reader locates the code by finding its border, so a code drawn edge-to-edge on a busy or dark
  // background often will not scan at all. Drawn on a light tile inside the slot rather than by
  // shrinking the slot, so the code still lands exactly where the artwork put it.
  const QUIET = 4;
  const shape = opts.shape ?? 'square';
  // The circle family ('disc', 'burst', 'badge') is a ROUND TILE with the code inset — the badge
  // look of the artwork's own printed discs and Canva's circle templates. The matrix itself stays
  // square (that is what a QR is); it is inscribed at side/√2 so every module sits inside the
  // circle, quiet zone included. 'frame' pulls the code in slightly so its border never eats the
  // quiet zone.
  const circular = shape === 'disc' || shape === 'burst' || shape === 'badge';
  // badge pulls the code in a touch further so its heavy rim never grazes the quiet zone
  
  const inner = shape === 'badge' ? size * 0.60
    : circular ? size / Math.SQRT2
    : shape === 'frame' ? size * 0.88 : size;
  const cell = inner / (n + QUIET * 2);
  const pad = (size - inner) / 2 + cell * QUIET;
  const round = shape === 'round';
  // The finder eyes: 7×7 at three corners. Kept square — see the note above.
  const inEye = (r: number, c: number) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  let marks = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!qr.isDark(r, c)) continue;
      const x = pad + c * cell;
      const y = pad + r * cell;
      if (round && !inEye(r, c)) {
        marks += `<circle cx="${(x + cell / 2).toFixed(2)}" cy="${(y + cell / 2).toFixed(2)}" r="${(cell / 2).toFixed(2)}"/>`;
      } else {
        marks += `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${cell.toFixed(2)}" height="${cell.toFixed(2)}"/>`;
      }
    }
  }
  const fill = opts.color === 'pink' ? '#dd1d8a' : '#101014';
  // THE TILE IS NOT OPTIONAL, and it is why this function paints its own background. The door
  // signage is near-black; the first render of this feature drew a black code onto that black and
  // produced something that looked like a QR in the preview and decoded as nothing at all — caught
  // only by rasterising the sheet and running a real decoder over it. Both inks need light behind
  // them, so the code always sits on white, which is also exactly what covers the artwork's own
  // printed code underneath.
  const c = size / 2;
  const decor: string[] = [];
  if (circular) {
    decor.push(`<circle cx="${c}" cy="${c}" r="${c}" fill="#ffffff"/>`);
    if (shape === 'disc') {
      decor.push(`<circle cx="${c}" cy="${c}" r="${c * 0.96}" fill="none" stroke="${fill}" stroke-width="${Math.max(1, size / 90)}"/>`);
    }
    if (shape === 'badge') {
      // The bold-rim badge. A TRUE inverted disc (light modules on dark) was built first and CUT:
      // zbar cannot decode inverted codes, and a print some scanners refuse is a support ticket.
      // The heavy ring gives the dark-badge look while the code itself stays normal-polarity.
      const bw = size / 13;
      decor.push(`<circle cx="${c}" cy="${c}" r="${c - bw / 2}" fill="none" stroke="${fill}" stroke-width="${bw}"/>`);
    }
    if (shape === 'burst') {
      // Radial dashes riding the rim — decoration outside the inscribed code, never over it.
      const r1 = c * 0.90, r2 = c * 0.985;
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        decor.push(`<line x1="${(c + r1 * Math.cos(a)).toFixed(2)}" y1="${(c + r1 * Math.sin(a)).toFixed(2)}"`
          + ` x2="${(c + r2 * Math.cos(a)).toFixed(2)}" y2="${(c + r2 * Math.sin(a)).toFixed(2)}"`
          + ` stroke="${fill}" stroke-width="${Math.max(1, size / 110)}" stroke-linecap="round"/>`);
      }
    }
  } else {
    decor.push(`<rect width="${size}" height="${size}" fill="#ffffff"/>`);
    if (shape === 'frame') {
      const bw = Math.max(1.5, size / 40);
      decor.push(`<rect x="${bw / 2}" y="${bw / 2}" width="${size - bw}" height="${size - bw}"`
        + ` rx="${size / 14}" fill="none" stroke="${fill}" stroke-width="${bw}"/>`);
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="${fill}">`
    + `${decor.join('')}${marks}</svg>`;
}
